import type { FormEvent, PropsWithChildren } from "react";
import { memo, useRef } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

import classes from "./styles.module.scss";

import type { IBaseFormProps } from ".";

import { Button } from "@components/atoms/button";
import { Icon } from "@/components/atoms/icons";
import { useUIStore } from "@/stores/uiStore";
import { useNotifyValidateForm } from "@/hooks";

export const Form = memo(function Form<T>({
  noButtons,
  isLoading,
  onCancel = "goBack",
  separator = true,
  extraButton,
  className,
  forceMode,
  onBack = "goBack",
  replaceEditLink = true,
  cancelBtnTitle,
  extraEndButton,
  ...props
}: PropsWithChildren<IBaseFormProps<T>>) {
  const { t } = useTranslation("form");
  const storeMode = useUIStore((state) => state.pageMode);
  const mode = forceMode || storeMode;
  const navigate = useNavigate();
  const submitTitle =
    props.submitBtnTitle ||
    (mode === "create"
      ? "register"
      : props.editButtonTitle || "recordInformationAndChanges");

  const formRef = useRef<HTMLFormElement>(null);

  const { notifyValidateForm } = useNotifyValidateForm(
    props?.validateForm,
    formRef,
  );

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    props.onSubmit?.(event);

    notifyValidateForm();
  }

  function handleEdit() {
    if (props.editLink) {
      navigate(props.editLink, { replace: replaceEditLink });
    }
  }

  function handleCancel() {
    if (onCancel === "goBack") {
      navigate(-1);
    } else {
      onCancel?.();
    }
  }
  function handleBack() {
    if (onBack === "goBack") {
      navigate(-1);
    } else {
      onBack?.();
    }
  }

  const createAndEditModeButton = (
    <>
      {!props.noCancel && (
        <Button
          className={classes.btn}
          mode={"cancel"}
          onClick={handleCancel}
          label={t(cancelBtnTitle || "cancelOperation")}
          {...props.cancelBtnProps}
          disabled={isLoading}
        />
      )}
      {extraButton}
      {!props.noSubmit && (
        <Button
          isLoading={isLoading}
          disabled={isLoading}
          mode={"submit"}
          type={"submit"}
          label={t(submitTitle)}
          {...props.submitBtnProps}
          className={classes.btn}
        />
      )}
      {extraEndButton}
    </>
  );
  const viewModeButton = (
    <>
      {!props.noCancel && (
        <Button
          className={classes.btn}
          mode={"default"}
          variant={"outlined"}
          label={t("back")}
          {...props.backBtnProps}
          onClick={handleBack}
          disabled={isLoading}
        />
      )}
      {extraButton}
      {!props.noEdit && (
        <Button
          type={"button"}
          mode={"main"}
          icon={<Icon name={"PenNewSquare"} />}
          label={t("edit")}
          className={classes.btn}
          onClick={handleEdit}
        />
      )}
    </>
  );

  return (
    <>
      <form
        onSubmit={onSubmit}
        className={className}
        autoComplete={"off"}
        ref={formRef}
      >
        <>
          {props.children}
          {!noButtons && (
            <div
              className={clsx([
                classes.btnContainer,
                separator && "border-top py-2m5",
              ])}
            >
              {mode === "view" && viewModeButton}
              {mode !== "view" && createAndEditModeButton}
            </div>
          )}
        </>
      </form>
    </>
  );
});
